/**
 * The icon set, as a lookup table.
 *
 * `navigation.ts` names an icon by string so the menu stays pure data (principle 9) — no JSX and
 * no component imports leak into the navigation definition.
 */
import {
  AlertTriangle,
  /* F48: dempet tråd. */
  BellOff,
  /* Control Room (sak 238, 0478). */
  Activity,
  ScrollText,
  ShieldCheck,
  UserCog,
  Facebook,
  Instagram,
  Linkedin,
  Minus,
  Music,
  Twitch,
  Twitter,
  Video,
  Youtube,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Ban,
  BedDouble,
  Briefcase,
  Building2,
  Bus,
  Calendar,
  CalendarDays,
  CalendarRange,
  Car,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Columns3,
  Bell,
  CircleAlert,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  GripVertical,
  Hash,
  Heading,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Monitor,
  Moon,
  Mic2,
  MoreHorizontal,
  Paperclip,
  Smile,
  Music4,
  Package,
  Pencil,
  Radio as RadioIcon,
  Speaker,
  UtensilsCrossed,
  Wrench,
  Plane,
  Phone,
  Plus,
  RefreshCw,
  Reply,
  Route,
  Search,
  Settings,
  Scale,
  Ship,
  Star,
  Sun,
  Ticket,
  TrainFront,
  Trash2,
  Undo2,
  User,
  Users,
  X,
  ArrowRightLeft,
  ArrowRightFromLine,
  ArrowRightToLine,
  Download,
  File as FileIcon,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileVideo,
  Folder,
  Link2,
  Upload,
  /* B5: «Koble til AI». */
  Bot,
  LogIn,
  MessageSquare,
  Plug,
  Sparkles,
  SquareTerminal,
  /* Tilgang-skuffen (ADVANCE-TILGANG.md §3–4): «Del», «Send invitasjon» og info-linjene. */
  Share,
  Send,
  Info,
  type LucideIcon,
} from 'lucide-react';

export const ICONS = {
  overview: LayoutDashboard,
  inbox: Inbox,
  tasks: ListChecks,
  contacts: Users,
  // Singular, and deliberately not the same glyph as `contacts`: one is a register of other
  // people, the other is you. They sit two centimetres apart in the shell.
  profile: User,
  organization: Building2,
  booking: CalendarRange,
  /* Programpostens Oversikt: fire nøkkeltall med ikon (PERFORMANCE.md, fane 1). */
  travel: Plane,
  // THE FIVE WAYS A JOURNEY MOVES, and they exist so the Travel tab can stop writing the word.
  // A leg's mode was a text cell repeating «Fly» down twelve rows; a glyph with the mode's own
  // name as its accessible label says the same in one square, and the column then scans.
  //
  // There is no separate `flight` entry: `travel` above IS the plane, and the tab icon being the
  // same glyph as the flight mode is not a collision — the tab is called Travel because a flight
  // is the thing it is mostly about. A second Plane under another name would be two names for
  // one shape, which is how an icon set stops being a vocabulary.
  train: TrainFront,
  bus: Bus,
  car: Car,
  boat: Ship,
  // «Annet» — a journey somebody has recorded without naming the vehicle. A route line rather
  // than a question mark: the leg is known, its means is not.
  transport: Route,
  // A BOOKING REFERENCE, which is not the same thing as the flight number beside it. One
  // identifies the vehicle (SK4108, printed on the departure board), the other identifies the
  // purchase (a PNR, printed on nothing anybody meets at an airport) — so the ticket stub marks
  // the second and the first stands bare in the mono face it is written in.
  reference: Ticket,
  stay: BedDouble,
  documents: FileText,
  festival: Music4,
  // `festivals.kind` is ONE column with two values (decision 0010), so the difference between a
  // festival and a venue has to be carried by something other than the data — and it cannot be
  // colour (DESIGN.md §3, "aldri farge alene"). A shape does it: a festival recurs and is about
  // what is played, a venue is a place that stays put. Not `organization`'s Building2, which is
  // already the company that OWNS both.
  venue: MapPin,
  mail: Mail,
  /* Hamburgeren som henter fram sidemenyen under 900 px (NAVIGATION.md, «Smal skjerm»). */
  menu: Menu,
  settings: Settings,
  signOut: LogOut,
  /** Et telefonnummer man skal ringe — bookingagentens, på artistkortet. */
  phone: Phone,
  plus: Plus,
  /* Ett pass tilbake av flere — se akkrediteringslista. Plus sin makker, aldri alene. */
  minus: Minus,
  check: Check,
  chevronDown: ChevronDown,
  /** Moving a row up a manual list — see `StagesPanel`, where it replaced a sort-order field. */
  chevronUp: ChevronUp,
  /** Ett hakk tilbake i en rekke — årgangene på bookingbrettet blas med disse to. */
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  close: X,
  search: Search,
  spinner: Loader2,
  retry: RefreshCw,
  warning: AlertTriangle,
  alert: CircleAlert,
  /* Varselbjella i skallet (J3). Den var en CircleAlert, og et utropstegn i en sirkel sier
     «noe er galt» — mens en bjelle sier «noe har skjedd». Det er to forskjellige beskjeder. */
  bell: Bell,
  /* F48 (pakke 3): en dempet tråd. */
  bellOff: BellOff,
  external: ExternalLink,
  back: ArrowLeft,
  light: Sun,
  dark: Moon,
  system: Monitor,
  /*
   * PROMO-LENKENES TJENESTER (Atle, 2. september): «legg til ikoner på alle de typiske sosiale
   * medier og slikt. Kommer det noen rar nettside er det kun tekst.»
   *
   * Which host gets which glyph is `promo-link-icon.ts` — this is only the vocabulary. Four of
   * them are not brand marks: `music` and `video` stand for Spotify, TikTok and the rest that
   * Lucide draws no logo for, and they say what the link IS rather than who owns it.
   */
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
  twitch: Twitch,
  music: Music,
  video: Video,
  reveal: Eye,
  conceal: EyeOff,
  // Read-only is signalled by this SHAPE, not by a paler border: a value you may read but not
  // change has to stay distinguishable with the colour turned off (DESIGN.md §3).
  lock: Lock,
  // Sort direction is signalled by SHAPE, never by colour alone (DESIGN.md §3): an arrow that
  // points up, an arrow that points down, and a neutral glyph for a sortable column at rest.
  sortAsc: ArrowUp,
  sortDesc: ArrowDown,
  sortNone: ChevronsUpDown,
  undo: Undo2,
  more: MoreHorizontal,
  /* Verktoeyene under skrivefeltet (blokk C6). */
  paperclip: Paperclip,
  smile: Smile,
  // EDIT IS A PENCIL, AND IT IS NOT A GEAR. Atle decided on 28 August that the gear means
  // exactly one thing — "go to the settings page for this" (TIL-GODKJENNING.md, question 10) —
  // so a row action that opens a dialog ABOUT the row cannot borrow it. `DepartmentList` and
  // `TagManager` still draw `settings` for their row edit; they predate the rule, and moving
  // them is not this pass's change to make.
  edit: Pencil,
  trash: Trash2,
  /*
   * ØNSKELISTA I BOOKINGREGISTERET. En stjerne og ikke et hjerte: Greenroom bruker stjerne, og
   * ordet i produktet er «ønskeliste» — en artist man vil tilbake til, ikke en man er glad i.
   */
  star: Star,
  /*
   * AGENTEN SOM REPRESENTERER ARTISTEN (ARTISTS.md §5.1).
   *
   * En koffert og ikke et menneskeikon: `profile` og `contacts` er allerede opptatt av MENNESKER,
   * og linja i artisthodet sier ikke «en person» — den sier «den som har jobben». Kofferten er
   * yrket, som er nettopp forskjellen mellom manageren og trommeslageren i den samme lista.
   */
  agent: Briefcase,
  /* Sammenlign: en skålvekt. To ting holdt opp mot hverandre, som er nøyaktig handlingen. */
  compare: Scale,
  clock: Clock,
  cancelled: Ban,
  // COPYING A STRUCTURE — AND NOT A GEAR EITHER. Two sheets, one behind the other: the glyph
  // says "this exists once and will exist twice" without claiming a direction, which is what a
  // clone is. The rule of 28 August rules the gear out (copying last year's departments into
  // this year is not "go to the settings page for this"), and `plus` would promise one new thing
  // rather than a tree that already has a shape.
  copy: Copy,
  /* Å svare på én melding i en samtale — se ChatMessageList. */
  reply: Reply,
  // WHICH ÅRGANG YOU ARE IN, and the one icon in the product that stands beside a bare numeral.
  // DESIGN.md §5 forbids "et ikon ved siden av en etikett som allerede sier det samme" — this is
  // the opposite case: the label is "2027" and nothing else, because Atle's rule for the control
  // is no words and no "Årgang:" prefix. Four digits alone in a header could be a count, a
  // budget or a room number; a calendar is what makes them a YEAR at a glance, which is the
  // whole reason the control is visible without being opened.
  //
  // Not `booking`'s CalendarRange: that glyph is the Booking module's own in the sidebar, and it
  // draws a SPAN of days — which is what an årgang's dates are, one card below this control.
  // A year is a page of the calendar, not a range on it.
  year: CalendarDays,
  // THE ORGANISATION'S CALENDAR — the totalkalender in the sidebar, beside Oppgaver.
  //
  // The third calendar glyph in this set, and the three are three different objects.
  // `booking`'s CalendarRange draws a SPAN of days, which is what an årgang is. `year` above is
  // one page of the calendar, standing beside a bare "2027". This one is the calendar itself:
  // the plain month, which is exactly what the page behind it draws.
  calendar: Calendar,
  // THE GRIP ON A ROW THAT CAN BE MOVED — six dots, and nothing else in the set looks like it.
  //
  // DESIGN.md §5 forbids "et ikon ved siden av en etikett som allerede sier det samme"; this one
  // has no label beside it to repeat, because a running-order row's handle IS the affordance. It
  // is the one glyph in the product that says "pick me up", and it has to be distinguishable from
  // `more`'s three dots two cells to its right — three across versus six in two columns.
  drag: GripVertical,

  // KOLONNEVELGEREN (ordren Del 3): tre loddrette felter — tabellen sett ovenfra. Den står
  // ved siden av ordet «Kolonner» og ikke alene, fordi ikonet er en gjenkjenning og ordet er
  // svaret.
  columns: Columns3,

  /*
   * RESSURSTYPENES TI IKONER (UX-ordren A4 og B5).
   *
   * Fire fantes fra før under andre navn — `car`, `stay`, `ticket` og et generisk — så bare de seks
   * som manglet er lagt til. Broen fra kontraktens `ResourceIcon` til disse navnene ligger i
   * `features/resources/resource-icon.ts`; se den for hvorfor de to enumene ikke er den samme.
   */
  stage: Mic2,
  meal: UtensilsCrossed,
  equipment: Speaker,
  radio: RadioIcon,
  box: Package,
  tool: Wrench,

  /*
   * FILUTFORSKEREN (QA 12, #15). Ikonet i venstre kolonne er det eneste som skiller en mappe fra
   * en fil i en liste der de står om hverandre, og filtypene er de fem en festival faktisk laster
   * opp: PDF og dokumenter, regneark, bilder, lyd og video. Alt annet får det nøytrale arket.
   */
  folder: Folder,
  file: FileIcon,
  fileImage: FileImage,
  fileSheet: FileSpreadsheet,
  fileAudio: FileAudio,
  fileVideo: FileVideo,
  fileArchive: FileArchive,
  download: Download,
  upload: Upload,
  moveTo: ArrowRightLeft,
  link: Link2,

  /*
   * SKJEMABYGGERENS FELTTYPER (QA13 runde 2, #15): «Felttype velges med ikon + navn».
   *
   * Ti av tolv typer hadde allerede et navn i denne tabellen — `calendar`, `check`, `tasks`,
   * `folder`, `clock`, `upload` og resten. Bare disse to manglet, og de er lagt til her framfor
   * å bli tegnet i skjemabyggeren: en type som får sitt eget glyf i én komponent er en type som
   * ser annerledes ut neste gang noen tegner den.
   */
  number: Hash,
  heading: Heading,

  /* «Overganger» (28. september): Inn er en pil inn mot streken, Ut en pil ut fra den. Byttet er moveTo. */
  moveIn: ArrowRightToLine,
  moveOut: ArrowRightFromLine,

  /* Control Room (sak 238, 0478): menypunktet nederst, og undermenyens egne. */
  controlRoom: ShieldCheck,
  platformAdmins: UserCog,
  operations: Activity,
  activityLog: ScrollText,
  /*
   * «KOBLE TIL AI» (B5). `ai` er ikonet i toppen; `aiAssistant` er det NØYTRALE ikonet hver
   * leverandør får til logoen er godkjent (279.13 a) — samme glyf for alle fem, så ingen ser ut som
   * merkevaren sin. Resten er stegene i veiledningene.
   */
  ai: Sparkles,
  aiAssistant: Bot,
  signIn: LogIn,
  terminal: SquareTerminal,
  chat: MessageSquare,
  plug: Plug,
  /*
   * TILGANG-SKUFFEN (ADVANCE-TILGANG.md §3–4). `share` er «Del» i advancens header — ikke `upload`,
   * som er å laste opp en fil. `info` er en forklaring, ikke et varsel (`alert`).
   */
  share: Share,
  send: Send,
  info: Info,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICONS;

export function iconFor(name: IconName): LucideIcon {
  return ICONS[name];
}
